// Single source of truth for bot-scheduling windows on the frontend.
//
// The backend (calliope-backend) mirrors these values in
// `src/constants/scheduling.ts`. If you change a value here, update that
// file as well — they must agree so the UI's validation messages match
// what the API will accept.

// Minimum lead time for a scheduled bot. Recall.ai requires > 10 min; we
// pad to 11 to absorb request latency between the client's "now" and the
// upstream's "now".
export const SCHEDULED_BOT_MIN_LEAD_MS = 11 * 60 * 1000;

// Maximum lead time for a scheduled bot. Caps how far in the future a
// bot can be booked, which keeps the LiveKit room emptyTimeout bounded
// and avoids accidentally reserving machines for long-forgotten bookings.
export const SCHEDULED_BOT_MAX_LEAD_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

// Convenience: number of days in the max-lead window. Used by scheduling
// controls so dropdown options and validation agree.
export const SCHEDULE_DATE_WINDOW_DAYS = 30;

// Date/time picker helpers. The browser's datetime-local and time controls
// speak user-local wall-clock strings, so these helpers deliberately avoid
// attaching a timezone offset.
const pad = (n: number) => String(n).padStart(2, '0');

export const formatForDatetimeLocal = (
  date: Date | null | undefined,
): string => {
  if (!date || Number.isNaN(date.getTime())) return '';
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

export const splitJoinAtLocal = (
  value: unknown,
): {date: string; time: string} => {
  if (!value || typeof value !== 'string') return {date: '', time: ''};
  const [date = '', time = ''] = value.split('T');
  return {date, time: time.slice(0, 5)};
};

export const combineJoinAtLocal = (
  date: string | null | undefined,
  time: string | null | undefined,
): string => {
  if (!date || !time) return '';
  return `${date}T${time}`;
};

export const buildDateOptions = (
  daysAhead: number = SCHEDULE_DATE_WINDOW_DAYS,
): {key: string; label: string}[] => {
  const options: {key: string; label: string}[] = [];
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const weekdayFmt = new Intl.DateTimeFormat(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
  for (let i = 0; i <= daysAhead; i += 1) {
    const d = new Date(today);
    d.setDate(today.getDate() + i);
    const key = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    const weekday = weekdayFmt.format(d);
    const prefix = i === 0 ? 'Today' : i === 1 ? 'Tomorrow' : null;
    options.push({key, label: prefix ? `${prefix} · ${weekday}` : weekday});
  }
  return options;
};

export const defaultLaterJoinAt = () => {
  const d = new Date(Date.now() + 30 * 60 * 1000);
  d.setSeconds(0, 0);
  return d;
};

export const getUserTimezoneLabel = () => {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const short = new Intl.DateTimeFormat('en-US', {timeZoneName: 'short'})
      .formatToParts(new Date())
      .find(p => p.type === 'timeZoneName')?.value;
    return short ? `${tz} (${short})` : tz;
  } catch {
    return '';
  }
};
